<template>
  <v-container class="pt-0" fluid>
    <!-- Filters -->
    <v-row class="align-center">
      <v-col v-if="!typeSlug" cols="auto">
        <v-card-title class="pt-0 pl-0">
          Sorting
        </v-card-title>
      </v-col>
      <v-spacer />
      <v-col cols="auto" style="min-width: 220px;">
        <select-project
          v-model="entity_id"
          label="Select project"
          @input="onProjectInput"
        />
      </v-col>
      <v-col v-if="!typeSlug" cols="auto" class="filter-col">
        <v-select
          v-model="filters.type_id"
          :items="types"
          item-text="name"
          item-value="type_id"
          label="Type"
          clearable
          hide-details
          :disabled="!entity_id"
          @change="onTypeChange"
        />
      </v-col>
      <v-col cols="auto" class="filter-col">
        <v-select
          v-model="filters.category_id"
          :items="categoryOptions"
          item-text="name"
          item-value="category_id"
          label="Category"
          hide-details
          :disabled="!filters.type_id"
          @change="loadData"
        />
      </v-col>
      <v-col cols="auto" class="filter-col">
        <v-select
          v-model="filters.dimension"
          :items="dimensionOptions"
          label="Dimension"
          hide-details
          :disabled="!filters.type_id"
          @change="onDimensionChange"
        />
      </v-col>
      <v-col v-if="filters.dimension && filters.dimension !== 'default'" cols="auto" class="filter-col">
        <v-select
          v-model="filters.dimension_value"
          :items="dimensionValueOptions"
          item-text="text"
          item-value="value"
          label="Dimension Value"
          hide-details
          :disabled="!filters.dimension"
          @change="loadData"
        />
      </v-col>
    </v-row>

    <!-- Actions -->
    <v-row v-if="canLoad" class="mt-0">
      <v-col cols="auto" class="pt-0">
        <v-btn
          color="primary"
          :loading="isLoading"
          @click="addDialogOpen = true"
        >
          Add Item
        </v-btn>
      </v-col>
      <v-col cols="auto" class="pt-0">
        <v-btn
          outlined
          :loading="isCopying"
          @click="copyDialogOpen = true"
        >
          Copy from...
        </v-btn>
      </v-col>
    </v-row>

    <!-- Sorting list -->
    <v-row>
      <v-col cols="12" class="pt-0">
        <v-card>
          <v-data-table
            class="cms-sorting-table"
            :headers="headers"
            :items="items"
            :loading="isLoading"
            item-key="item_sorting_id"
            :sort-by="['sort_order']"
            hide-default-footer
            :items-per-page="-1"
            fixed-header
            :height="tableHeight"
          >
            <template #header.sort_order="{ header }">
              {{ header.text }}
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-icon small v-bind="attrs" v-on="on" class="ml-1">mdi-information-outline</v-icon>
                </template>
                Lower sort_order = higher position. Use arrows to reorder.
              </v-tooltip>
            </template>
            <template #item.item_name="{ item }">
              {{ item.item_name || item.item_slug || `Item #${item.item_id}` }}
            </template>
            <template #item.sort_order="{ item }">
              <v-chip small outlined>
                {{ item.sort_order }}
              </v-chip>
            </template>
            <template #item.dimension_value="{ item }">
              <span>{{ item.dimension_value }}</span>
              <v-chip v-if="item.dimension_value === 'default'" x-small color="blue" outlined class="ml-1">
                Default
              </v-chip>
            </template>
            <template #item.actions="{ item, index }">
              <v-icon
                small
                class="mr-1"
                :disabled="index === 0"
                @click="moveUp(index)"
              >
                mdi-arrow-up
              </v-icon>
              <v-icon
                small
                class="mr-1"
                :disabled="index === items.length - 1"
                @click="moveDown(index)"
              >
                mdi-arrow-down
              </v-icon>
              <v-icon
                small
                color="error"
                @click="onDelete(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <template #no-data>
              <span v-if="canLoad" class="grey--text">No sorting entries. Add items or copy from another dimension value.</span>
              <span v-else class="grey--text">Select type, dimension and dimension value to load sorting.</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Add Item Dialog -->
    <v-dialog v-model="addDialogOpen" max-width="700">
      <v-card>
        <v-card-title>Add Items to Sorting</v-card-title>
        <v-divider />
        <v-card-text class="pt-4 pb-0">
          <v-row dense>
            <v-col cols="5">
              <v-text-field
                v-model="addSearch"
                label="Search by name"
                clearable
                dense
                outlined
                hide-details
                prepend-inner-icon="mdi-magnify"
              />
            </v-col>
            <v-col cols="3">
              <v-select
                v-model="addFilterStatus"
                :items="['draft', 'published', 'archived']"
                label="Status"
                clearable
                dense
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="4">
              <v-select
                v-model="addFilterCategory"
                :items="categories"
                item-text="name"
                item-value="category_id"
                label="Category"
                clearable
                dense
                outlined
                hide-details
              />
            </v-col>
          </v-row>
          <v-row dense class="mt-2">
            <v-col>
              <v-autocomplete
                v-model="addItemIds"
                :items="filteredAvailableItems"
                :loading="isLoadingItems"
                item-text="name"
                item-value="item_id"
                label="Select items"
                multiple
                chips
                small-chips
                deletable-chips
                outlined
                dense
                hide-details
              />
            </v-col>
          </v-row>
          <div class="mt-2 grey--text text-caption">
            {{ filteredAvailableItems.length }} items available
            <span v-if="addItemIds.length"> · {{ addItemIds.length }} selected</span>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn
            text
            color="primary"
            :disabled="!filteredAvailableItems.length"
            :loading="isAdding"
            @click="onAddAllFiltered"
          >
            Add All Filtered ({{ filteredAvailableItems.length }})
          </v-btn>
          <v-spacer />
          <v-btn text @click="addDialogOpen = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!addItemIds.length"
            :loading="isAdding"
            @click="onAddSelected"
          >
            Add Selected ({{ addItemIds.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Copy From Dialog -->
    <v-dialog v-model="copyDialogOpen" max-width="500">
      <v-card>
        <v-card-title>Copy Sorting From</v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-select
            v-model="copyCategoryId"
            :items="categoryOptions"
            item-text="name"
            item-value="category_id"
            label="Source category"
            outlined
            dense
            hide-details
            class="mb-4"
          />
          <v-select
            v-model="copySource"
            :items="copySourceOptions"
            item-text="text"
            item-value="value"
            label="Source dimension"
            outlined
            dense
            hide-details
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="copyDialogOpen = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!copySource" :loading="isCopying" @click="onCopyFrom">
            Copy
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { API } from '@/utils/api'
import SelectProject from '@/components/core/SelectProject/SelectProject'

const DIMENSION_OPTIONS = ['default', 'country', 'locale']

export default {
  components: {
    SelectProject,
  },
  props: {
    typeSlug: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      isCopying: false,
      isLoadingItems: false,
      entity_id: null,
      filters: {
        type_id: null,
        category_id: 0,
        dimension: null,
        dimension_value: null,
      },
      types: [],
      categories: [],
      countries: [],
      locales: [],
      items: [],
      allItems: [],
      addDialogOpen: false,
      addItemIds: [],
      addSearch: '',
      addFilterStatus: null,
      addFilterCategory: null,
      isAdding: false,
      copyDialogOpen: false,
      copySource: null,
      copyCategoryId: 0,
      dimensionOptions: DIMENSION_OPTIONS,
      headers: [
        { text: 'Item', value: 'item_name' },
        { text: 'Sort Order', value: 'sort_order', width: 120 },
        { text: 'Dimension Value', value: 'dimension_value', width: 180 },
        { text: 'Updated', value: 'updated', width: 160 },
        { text: 'Actions', value: 'actions', sortable: false, width: 140 },
      ],
    }
  },
  computed: {
    tableHeight() {
      return 'calc(100vh - 251px)'
    },
    canLoad() {
      return this.filters.type_id && this.filters.dimension && this.filters.dimension_value
    },
    categoryOptions() {
      return [
        { category_id: 0, name: 'All / No category' },
        ...this.categories,
      ]
    },
    dimensionValueOptions() {
      const defaultOpt = { text: 'default', value: 'default' }
      if (this.filters.dimension === 'country') {
        return [
          defaultOpt,
          ...this.countries.map(c => ({ text: `${c.code} — ${c.name}`, value: c.code })),
        ]
      }
      if (this.filters.dimension === 'locale') {
        return [
          defaultOpt,
          ...this.locales.map(l => ({ text: `${l.code} — ${l.name}`, value: l.code })),
        ]
      }
      return [defaultOpt]
    },
    copySourceOptions() {
      const options = this.dimensionValueOptions
        .filter(o => o.value !== this.filters.dimension_value)
        .map(o => ({ text: o.text, value: `${this.filters.dimension}:${o.value}` }))
      if (this.filters.dimension !== 'default') {
        options.unshift({ text: 'Default (global)', value: 'default:default' })
      }
      return options
    },
    existingItemIds() {
      return new Set(this.items.map(i => i.item_id))
    },
    availableItems() {
      return this.allItems.filter(i => !this.existingItemIds.has(i.item_id))
    },
    filteredAvailableItems() {
      let result = this.availableItems
      if (this.addSearch) {
        const q = this.addSearch.toLowerCase()
        result = result.filter(i => (i.name || '').toLowerCase().includes(q) || (i.slug || '').toLowerCase().includes(q))
      }
      if (this.addFilterStatus) {
        result = result.filter(i => i.status === this.addFilterStatus)
      }
      if (this.addFilterCategory) {
        result = result.filter(i => (i.category_ids || []).includes(this.addFilterCategory))
      }
      return result
    },
    itemNameById() {
      const map = {}
      for (const i of this.allItems) {
        map[i.item_id] = i.name || i.slug
      }
      return map
    },
  },
  watch: {
    typeSlug() {
      if (this.entity_id) {
        this.applyTypeSlug()
        this.loadCategories()
        this.loadData()
      }
    },
    addDialogOpen(val) {
      if (val) {
        this.addItemIds = []
        this.addSearch = ''
        this.addFilterStatus = null
        this.addFilterCategory = null
        this.loadAllItems()
      }
    },
    copyDialogOpen(val) {
      if (val) {
        this.copySource = null
        this.copyCategoryId = this.filters.category_id
      }
    },
  },
  methods: {
    onProjectInput(val) {
      if (val) {
        this.loadTypes().then(() => {
          if (this.typeSlug) {
            this.applyTypeSlug()
            this.loadCategories()
            this.loadData()
          }
        })
      }
    },
    applyTypeSlug() {
      if (!this.typeSlug) {
        return
      }
      const type = this.types.find(t => t.slug === this.typeSlug)
      if (type) {
        this.filters.type_id = type.type_id
      }
    },
    onTypeChange() {
      this.filters.category_id = 0
      this.loadCategories()
      this.loadData()
    },
    onDimensionChange() {
      if (this.filters.dimension === 'default') {
        this.filters.dimension_value = 'default'
        this.loadData()
        return
      }
      this.filters.dimension_value = null
      if (this.filters.dimension === 'country' && !this.countries.length) {
        this.loadCountries()
      }
      if (this.filters.dimension === 'locale' && !this.locales.length) {
        this.loadLocales()
      }
    },
    loadTypes() {
      return API.call('/cms/type/list.json', {}).then(r => {
        const all = r.data.items || []
        this.types = all.filter(t => t.kind !== 'fragment')
      })
    },
    loadCategories() {
      if (!this.entity_id || !this.filters.type_id) {
        this.categories = []
        return
      }
      API.call('/cms/category/list.json', {
        entity_id: this.entity_id,
        type_id: this.filters.type_id,
        is_active: true,
      }).then(r => {
        this.categories = r.data.items || []
      })
    },
    loadCountries() {
      API.call('/cms/country/list.json', { is_active: true }).then(r => {
        this.countries = r.data.items || []
      })
    },
    loadLocales() {
      this.$store.dispatch('cms/main/loadLocales').then(locales => {
        this.locales = locales
      }).catch(() => {
        this.locales = []
      })
    },
    loadData() {
      if (!this.canLoad) {
        this.items = []
        return
      }
      this.isLoading = true
      API.call('/cms/item-sorting/list.json', {
        entity_id: this.entity_id,
        category_id: this.filters.category_id,
        dimension: this.filters.dimension,
        dimension_value: this.filters.dimension_value,
      }).then(r => {
        const sortings = r.data.items || []
        this.items = sortings.map(s => ({
          ...s,
          item_name: this.itemNameById[s.item_id] || null,
        }))
        if (this.items.some(i => !i.item_name)) {
          this.loadItemNames()
        }
      }).finally(() => {
        this.isLoading = false
      })
    },
    loadItemNames() {
      const missingIds = this.items.filter(i => !i.item_name).map(i => i.item_id)
      if (!missingIds.length) {
        return
      }
      API.call('/cms/item/list.json', {
        entity_id: this.entity_id,
        type_id: this.filters.type_id,
        perpage: 1000,
      }).then(r => {
        const allItems = r.data.items || []
        this.allItems = allItems
        const nameMap = {}
        for (const item of allItems) {
          nameMap[item.item_id] = item.name || item.slug
        }
        this.items = this.items.map(s => ({
          ...s,
          item_name: nameMap[s.item_id] || s.item_name || `Item #${s.item_id}`,
        }))
      })
    },
    loadAllItems() {
      if (!this.entity_id || !this.filters.type_id) {
        return
      }
      this.isLoadingItems = true
      API.call('/cms/item/list.json', {
        entity_id: this.entity_id,
        type_id: this.filters.type_id,
        perpage: 1000,
      }).then(r => {
        this.allItems = r.data.items || []
      }).finally(() => {
        this.isLoadingItems = false
      })
    },
    moveUp(index) {
      if (index === 0) {
        return
      }
      const arr = [...this.items]
      const temp = arr[index]
      arr[index] = arr[index - 1]
      arr[index - 1] = temp
      this.reorder(arr)
    },
    moveDown(index) {
      if (index === this.items.length - 1) {
        return
      }
      const arr = [...this.items]
      const temp = arr[index]
      arr[index] = arr[index + 1]
      arr[index + 1] = temp
      this.reorder(arr)
    },
    reorder(arr) {
      const updates = []
      for (let i = 0; i < arr.length; i++) {
        const newOrder = (i + 1) * 10
        if (arr[i].sort_order !== newOrder) {
          arr[i] = { ...arr[i], sort_order: newOrder }
          updates.push(arr[i])
        }
      }
      this.items = arr
      if (!updates.length) {
        return
      }
      this.isLoading = true
      Promise.all(
        updates.map(item =>
          API.call('/cms/item-sorting/upsert.json', {
            entity_id: this.entity_id,
            item_id: item.item_id,
            category_id: this.filters.category_id,
            dimension: this.filters.dimension,
            dimension_value: this.filters.dimension_value,
            sort_order: item.sort_order,
          })
        )
      ).then(() => {
        this.$showSuccess('Order updated')
      }).finally(() => {
        this.isLoading = false
      })
    },
    addItems(itemIds) {
      if (!itemIds.length) {
        return
      }
      const lastOrder = this.items.length ? this.items[this.items.length - 1].sort_order : 0
      this.isAdding = true
      Promise.all(
        itemIds.map((id, i) =>
          API.call('/cms/item-sorting/upsert.json', {
            entity_id: this.entity_id,
            item_id: id,
            category_id: this.filters.category_id,
            dimension: this.filters.dimension,
            dimension_value: this.filters.dimension_value,
            sort_order: lastOrder + (i + 1) * 10,
          })
        )
      ).then(() => {
        this.$showSuccess(`${itemIds.length} item(s) added`)
        this.addDialogOpen = false
        this.loadData()
      }).finally(() => {
        this.isAdding = false
      })
    },
    onAddSelected() {
      this.addItems(this.addItemIds)
    },
    onAddAllFiltered() {
      const ids = this.filteredAvailableItems.map(i => i.item_id)
      this.$root.$confirm('Confirm', `Add ${ids.length} items to sorting?`)
        .then(yes => {
          if (yes) {
            this.addItems(ids)
          }
        })
    },
    onDelete(item) {
      this.$root.$confirm('Confirm', `Remove "${item.item_name || item.item_id}" from sorting?`)
        .then(yes => {
          if (!yes) {
            return
          }
          this.isLoading = true
          API.call('/cms/item-sorting/delete.json', {
            entity_id: this.entity_id,
            item_id: item.item_id,
            category_id: this.filters.category_id,
            dimension: this.filters.dimension,
            dimension_value: this.filters.dimension_value,
          }).then(() => {
            this.$showSuccess('Removed from sorting')
            this.loadData()
          }).finally(() => {
            this.isLoading = false
          })
        })
    },
    onCopyFrom() {
      if (!this.copySource) {
        return
      }
      const [srcDimension, srcValue] = this.copySource.split(':')
      this.isCopying = true
      API.call('/cms/item-sorting/list.json', {
        entity_id: this.entity_id,
        category_id: this.copyCategoryId,
        dimension: srcDimension,
        dimension_value: srcValue,
      }).then(r => {
        const sourceItems = r.data.items || []
        if (!sourceItems.length) {
          this.$showSuccess('Source has no sorting entries')
          this.isCopying = false
          return
        }
        return Promise.all(
          sourceItems.map(s =>
            API.call('/cms/item-sorting/upsert.json', {
              entity_id: this.entity_id,
              item_id: s.item_id,
              category_id: this.filters.category_id,
              dimension: this.filters.dimension,
              dimension_value: this.filters.dimension_value,
              sort_order: s.sort_order,
            })
          )
        ).then(() => {
          this.$showSuccess(`Copied ${sourceItems.length} entries`)
          this.copyDialogOpen = false
          this.loadData()
        })
      }).finally(() => {
        this.isCopying = false
      })
    },
  },
}
</script>

<style>
.cms-sorting-table th {
  white-space: nowrap !important;
}
</style>

<style scoped>
.filter-col {
  min-width: 140px;
  max-width: 225px;
}
</style>